import { catalogs } from "@desfecha/i18n";
import type {
  ModelChoice,
  ModelOption,
  ModelProfile,
  ThinkingLevel,
} from "../api/aiModels";
import { locale, t, type MessageKey } from "../i18n";

/** Names and words for the AI models screens (decision 0037). */

export const PROVIDER_NAMES: Record<string, string> = {
  anthropic: "Claude",
  openai: "OpenAI",
  google: "Gemini",
};

export const providerName = (provider: string) =>
  PROVIDER_NAMES[provider] ?? provider;

const DATED = /-(\d{4}-\d{2}-\d{2}|\d{8})$/;

/**
 * A model's name as people say it: "claude-sonnet-5-5" is "Sonnet 5.5", "gpt-6.1-sol" is
 * "GPT-6.1 Sol", "gpt-4o-mini" is "GPT-4o mini", "gemini-3.8-flash" is "Gemini 3.8 Flash". The
 * catalog's own name wins when it has one.
 */
export function modelName(model: string, catalogName?: string | null): string {
  if (catalogName) return catalogName.replace(/^Claude /, "");
  const id = model.replace(DATED, "");
  const claude = /^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(.*)$/.exec(id);
  if (claude) {
    const family = claude[1]!.charAt(0).toUpperCase() + claude[1]!.slice(1);
    return `${family} ${claude[2]}${claude[3] ? `.${claude[3]}` : ""}${claude[4] ?? ""}`;
  }
  const gemini = /^gemini-([\d.]+)(.*)$/.exec(id);
  if (gemini)
    return `Gemini ${gemini[1]}${(gemini[2] ?? "")
      .split("-")
      .filter(Boolean)
      .map((w) => ` ${w.charAt(0).toUpperCase()}${w.slice(1)}`)
      .join("")}`;
  const gpt = /^gpt-([\d.]+[a-z]?)(.*)$/.exec(id);
  if (gpt)
    return `GPT-${gpt[1]}${(gpt[2] ?? "")
      .split("-")
      .filter(Boolean)
      .map((w, i) =>
        i === 0 ? ` ${w.charAt(0).toUpperCase()}${w.slice(1)}` : ` ${w}`,
      )
      .join("")}`;
  return id;
}

/** "Claude · Sonnet 5.5" */
export function modelLabel(
  choice: { provider: string; model: string },
  options?: ModelOption[],
): string {
  const named = options?.find(
    (o) => o.provider === choice.provider && o.model === choice.model,
  )?.name;
  return `${providerName(choice.provider)} · ${modelName(choice.model, named)}`;
}

export const thinkingName = (kind: "effort" | "reasoning") =>
  t(kind === "effort" ? "aimodels.effort" : "aimodels.reasoning");

export const levelName = (level: ThinkingLevel) =>
  t(`aimodels.level.${level}` as MessageKey);

/** Whether the model takes a temperature with the thinking level chosen. */
export function takesTemperature(
  profile: ModelProfile,
  effort: ThinkingLevel | undefined,
): boolean {
  return (
    profile.temperature === "always" ||
    (profile.temperature === "without_thinking" && effort === "none")
  );
}

/** The settings of a choice as chips: "Raciocínio: baixo", "Temperatura: 0", "Reserva: Sol". */
export function settingsChips(
  choice: ModelChoice,
  profile: ModelProfile,
): string[] {
  const chips: string[] = [];
  const p = choice.params ?? {};
  if (profile.thinking && p.effort)
    chips.push(
      `${thinkingName(profile.thinking.kind)}: ${levelName(p.effort).toLocaleLowerCase(locale.value)}`,
    );
  if (p.temperature !== undefined && takesTemperature(profile, p.effort))
    chips.push(t("aimodels.chip.temperature", { value: p.temperature }));
  if (p.fallback_model)
    chips.push(
      t("aimodels.chip.fallback", { model: modelName(p.fallback_model) }),
    );
  return chips;
}

export const surfaceName = (surface: string) => {
  const key = `ai.surface.${surface}` as MessageKey;
  return key in catalogs[locale.value] ? t(key) : surface;
};

export const surfaceHint = (surface: string) => {
  const key = `aimodels.hint.${surface}` as MessageKey;
  return key in catalogs[locale.value] ? t(key) : "";
};

/** The models that can do a task: its kind, and tools where the task needs them. */
export function optionsFor(
  options: ModelOption[],
  surface: { surface: string; needsTools: boolean },
): ModelOption[] {
  const mode =
    surface.surface === "knowledge.embed"
      ? "embedding"
      : surface.surface === "audio.transcribe"
        ? "audio_transcription"
        : "chat";
  return options.filter(
    (o) => o.mode === mode && (!surface.needsTools || o.tools),
  );
}

/**
 * The other provider's backup once the main model changes provider (decision 0040): the model
 * being left becomes it when it can do the task; a backup of the new main's provider goes.
 * Keys are "provider:model"; "" is none.
 */
export function backupAfterSwitch(input: {
  backup: string;
  left: string;
  next: string;
  offered: string[];
}): string {
  if (input.backup && !input.backup.startsWith(`${input.next}:`))
    return input.backup;
  return input.offered.includes(input.left) &&
    !input.left.startsWith(`${input.next}:`)
    ? input.left
    : "";
}
